import { motion } from 'framer-motion'
import type { MenuTheme } from '@/types'

// ============================================
// THEME DECORATIONS - Visual elements that make each theme unique
// ============================================

interface ThemeDecorationsProps {
  theme: MenuTheme
}

// ============================================
// BACKGROUND PATTERNS
// ============================================

export const BackgroundPattern = ({ theme }: ThemeDecorationsProps) => {
  switch (theme.backgroundPattern) {
    case 'marble':
      return <MarblePattern opacity={theme.backgroundPatternOpacity} />
    case 'grid':
      return <GridPattern opacity={theme.backgroundPatternOpacity} primaryColor={theme.primaryColor} secondaryColor={theme.secondaryColor} />
    case 'wood':
      return <WoodPattern opacity={theme.backgroundPatternOpacity} />
    case 'waves':
      return <WavesPattern opacity={theme.backgroundPatternOpacity} primaryColor={theme.primaryColor} />
    case 'snow':
      return <SnowPattern opacity={theme.backgroundPatternOpacity} />
    case 'radial':
    default:
      return <RadialPattern opacity={theme.backgroundPatternOpacity} />
  }
}

// Classic radial dots
const RadialPattern = ({ opacity }: { opacity: number }) => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      opacity,
      backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }}
  />
)

// Luxury marble texture
const MarblePattern = ({ opacity }: { opacity: number }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity }}>
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        <filter id="marble-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="4" result="noise" />
          <feDiffuseLighting in="noise" lightingColor="#D4AF37" surfaceScale="2">
            <feDistantLight azimuth="45" elevation="60" />
          </feDiffuseLighting>
        </filter>
      </defs>
      <rect width="100%" height="100%" filter="url(#marble-noise)" opacity="0.3" />
    </svg>
    {/* Gold veins */}
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200" preserveAspectRatio="none">
      <path d="M0,100 Q50,80 100,100 T200,100" stroke="rgba(212,175,55,0.15)" strokeWidth="0.5" fill="none" />
      <path d="M0,50 Q100,30 200,50" stroke="rgba(244,208,63,0.1)" strokeWidth="0.3" fill="none" />
      <path d="M0,150 Q100,170 200,150" stroke="rgba(212,175,55,0.12)" strokeWidth="0.4" fill="none" />
    </svg>
  </div>
)

// Neon cyberpunk grid
const GridPattern = ({ opacity, primaryColor, secondaryColor }: { opacity: number; primaryColor: string; secondaryColor: string }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity }}>
    {/* Perspective grid */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `
          linear-gradient(${primaryColor}22 1px, transparent 1px),
          linear-gradient(90deg, ${primaryColor}22 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        transform: 'perspective(500px) rotateX(60deg)',
        transformOrigin: 'center top',
      }}
    />
    {/* Horizontal scanlines */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, ${primaryColor}08 2px, ${primaryColor}08 4px)`,
      }}
    />
    {/* Glow line at horizon */}
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-1/3 left-0 right-0 h-px"
      style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, ${secondaryColor}, ${primaryColor}, transparent)` }}
    />
  </div>
)

// Rustic wood grain
const WoodPattern = ({ opacity }: { opacity: number }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity }}>
    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <pattern id="wood-grain" patternUnits="userSpaceOnUse" width="200" height="200">
          {/* Wood grain lines */}
          {[...Array(20)].map((_, i) => (
            <path
              key={i}
              d={`M0,${i * 10} Q50,${i * 10 + Math.sin(i) * 3} 100,${i * 10} T200,${i * 10}`}
              stroke="rgba(139,69,19,0.15)"
              strokeWidth="1"
              fill="none"
            />
          ))}
          {/* Knots */}
          <circle cx="80" cy="60" r="8" fill="rgba(101,67,33,0.1)" />
          <circle cx="150" cy="140" r="5" fill="rgba(101,67,33,0.08)" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#wood-grain)" />
    </svg>
  </div>
)

// Ocean waves
const WavesPattern = ({ opacity, primaryColor }: { opacity: number; primaryColor: string }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity }}>
    {/* Multiple wave layers */}
    {[0, 1, 2].map((layer) => (
      <motion.svg
        key={layer}
        className="absolute bottom-0 left-0 w-[200%] h-64"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        animate={{ x: [0, layer % 2 === 0 ? -720 : 720] }}
        transition={{ duration: 15 + layer * 5, repeat: Infinity, ease: "linear" }}
        style={{ opacity: 0.3 - layer * 0.08 }}
      >
        <path
          fill={primaryColor}
          d={`M0,${160 + layer * 30}L48,${170 + layer * 25}C96,${180 + layer * 20},192,${200 + layer * 15},288,${186 + layer * 20}C384,${173 + layer * 25},480,${128 + layer * 30},576,${122 + layer * 35}C672,${117 + layer * 30},768,${149 + layer * 25},864,${170 + layer * 20}C960,${192 + layer * 15},1056,${202 + layer * 10},1152,${181 + layer * 15}C1248,${160 + layer * 20},1344,${106 + layer * 25},1392,${80 + layer * 30}L1440,${53 + layer * 35}L1440,320L0,320Z`}
        />
      </motion.svg>
    ))}
  </div>
)

// Christmas snow
const SnowPattern = ({ opacity }: { opacity: number }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity }}>
    {/* Falling snow effect via CSS */}
    <style>{`
      @keyframes snowfall {
        0% { transform: translateY(-10px) rotate(0deg); }
        100% { transform: translateY(100vh) rotate(360deg); }
      }
      .snowflake {
        position: absolute;
        color: white;
        animation: snowfall linear infinite;
      }
    `}</style>
    {[...Array(30)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute text-white"
        initial={{
          x: `${Math.random() * 100}%`,
          y: -20,
          opacity: Math.random() * 0.5 + 0.3
        }}
        animate={{
          y: '100vh',
          rotate: [0, 360]
        }}
        transition={{
          duration: Math.random() * 10 + 10,
          repeat: Infinity,
          delay: Math.random() * 10,
          ease: 'linear'
        }}
        style={{
          fontSize: Math.random() * 12 + 8,
          left: `${Math.random() * 100}%`,
        }}
      >
        {i % 3 === 0 ? '❄' : i % 3 === 1 ? '❅' : '❆'}
      </motion.div>
    ))}
  </div>
)

// ============================================
// FLOATING DECORATIVE ELEMENTS
// ============================================

export const FloatingElements = ({ theme }: ThemeDecorationsProps) => {
  if (!theme.hasFloatingElements) return null

  switch (theme.decorativeElement) {
    case 'sparkles':
      return <GoldSparkles />
    case 'scanlines':
      return <NeonScanlines primaryColor={theme.primaryColor} secondaryColor={theme.secondaryColor} />
    case 'bubbles':
      return <OceanBubbles primaryColor={theme.primaryColor} />
    case 'snowflakes':
      return <ChristmasLights />
    case 'leaves':
      return null // Rustic doesn't need floating elements
    case 'orbs':
    default:
      return <GlowingOrbs primaryColor={theme.primaryColor} accentColor={theme.accentColor} />
  }
}

// Classic glowing orbs
const GlowingOrbs = ({ primaryColor, accentColor }: { primaryColor: string; accentColor: string }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.2, 1] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -top-1/4 -left-1/4 w-[60%] h-[60%] rounded-full blur-[120px]"
      style={{ backgroundColor: `${primaryColor}25` }}
    />
    <motion.div
      animate={{ x: [0, -20, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      className="absolute -bottom-1/4 -right-1/4 w-[50%] h-[50%] rounded-full blur-[100px]"
      style={{ backgroundColor: `${accentColor}20` }}
    />
  </div>
)

// Gold sparkles for luxury theme
const GoldSparkles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-amber-300"
        initial={{
          x: `${Math.random() * 100}%`,
          y: `${Math.random() * 100}%`,
          scale: 0,
          opacity: 0
        }}
        animate={{
          scale: [0, 1, 0],
          opacity: [0, 1, 0]
        }}
        transition={{
          duration: 2 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 4,
          ease: "easeInOut"
        }}
        style={{
          boxShadow: '0 0 6px 2px rgba(212,175,55,0.8)',
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
      />
    ))}
    {/* Larger sparkle bursts */}
    {[...Array(5)].map((_, i) => (
      <motion.div
        key={`burst-${i}`}
        className="absolute"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.5, 0], opacity: [0, 0.8, 0] }}
        transition={{
          duration: 3,
          repeat: Infinity,
          delay: i * 1.5,
          ease: "easeOut"
        }}
        style={{
          left: `${20 + i * 15}%`,
          top: `${10 + i * 20}%`,
        }}
      >
        <span className="text-2xl">✨</span>
      </motion.div>
    ))}
  </div>
)

// Neon scanlines and glitch effects
const NeonScanlines = ({ primaryColor, secondaryColor }: { primaryColor: string; secondaryColor: string }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Moving scanline */}
    <motion.div
      animate={{ y: ['-100%', '200%'] }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      className="absolute left-0 right-0 h-32"
      style={{
        background: `linear-gradient(to bottom, transparent, ${primaryColor}15, transparent)`
      }}
    />
    {/* Glitch flicker */}
    <motion.div
      animate={{ opacity: [0, 0.1, 0, 0.05, 0] }}
      transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 3 }}
      className="absolute inset-0"
      style={{ backgroundColor: secondaryColor }}
    />
    {/* Corner accents */}
    <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2" style={{ borderColor: primaryColor }} />
    <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2" style={{ borderColor: secondaryColor }} />
    <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2" style={{ borderColor: secondaryColor }} />
    <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2" style={{ borderColor: primaryColor }} />
  </div>
)

// Ocean bubbles
const OceanBubbles = ({ primaryColor }: { primaryColor: string }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full border"
        initial={{
          y: '110%',
          x: `${Math.random() * 100}%`,
          opacity: 0
        }}
        animate={{
          y: '-10%',
          opacity: [0, 0.6, 0]
        }}
        transition={{
          duration: 8 + Math.random() * 6,
          repeat: Infinity,
          delay: Math.random() * 8,
          ease: 'easeOut'
        }}
        style={{
          width: 8 + Math.random() * 20,
          height: 8 + Math.random() * 20,
          borderColor: `${primaryColor}40`,
          backgroundColor: `${primaryColor}10`,
          left: `${Math.random() * 100}%`,
        }}
      />
    ))}
  </div>
)

// Christmas lights
const ChristmasLights = () => {
  const colors = ['#DC2626', '#16A34A', '#D4AF37', '#DC2626', '#16A34A']
  return (
    <div className="absolute top-0 left-0 right-0 h-20 overflow-hidden pointer-events-none">
      {/* String of lights */}
      <svg className="absolute top-0 left-0 w-full h-12" viewBox="0 0 1000 50" preserveAspectRatio="none">
        <path
          d="M0,10 Q125,40 250,10 T500,10 T750,10 T1000,10"
          stroke="#2a2a2a"
          strokeWidth="2"
          fill="none"
        />
      </svg>
      {/* Light bulbs */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-4 rounded-full"
          animate={{
            opacity: [0.6, 1, 0.6],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.2,
            ease: "easeInOut"
          }}
          style={{
            backgroundColor: colors[i % colors.length],
            boxShadow: `0 0 10px 3px ${colors[i % colors.length]}80`,
            left: `${7 + i * 8}%`,
            top: i % 2 === 0 ? '20px' : '28px'
          }}
        />
      ))}
    </div>
  )
}

// ============================================
// PARTICLE SYSTEMS
// ============================================

export const ParticleSystem = ({ theme }: ThemeDecorationsProps) => {
  if (!theme.hasParticles) return null

  switch (theme.decorativeElement) {
    case 'sparkles':
      return <LuxuryDust />
    case 'scanlines':
      return <DigitalRain primaryColor={theme.primaryColor} />
    case 'bubbles':
      return null // Already handled by FloatingElements
    case 'snowflakes':
      return null // Already handled by BackgroundPattern
    default:
      return null
  }
}

// Luxury floating dust particles
const LuxuryDust = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-0.5 h-0.5 rounded-full bg-amber-200"
        animate={{
          y: [0, -30, 0],
          x: [0, Math.random() * 20 - 10, 0],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{
          duration: 4 + Math.random() * 4,
          repeat: Infinity,
          delay: Math.random() * 4,
          ease: "easeInOut"
        }}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
      />
    ))}
  </div>
)

// Digital rain for neon theme
const DigitalRain = ({ primaryColor }: { primaryColor: string }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-px"
        style={{
          background: `linear-gradient(to bottom, transparent, ${primaryColor}, transparent)`,
          height: 50 + Math.random() * 100,
          left: `${Math.random() * 100}%`,
        }}
        animate={{ y: ['-20%', '120%'] }}
        transition={{
          duration: 2 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
          ease: 'linear'
        }}
      />
    ))}
  </div>
)

// ============================================
// HERO SECTION DECORATIONS
// ============================================

export const HeroDecorations = ({ theme }: ThemeDecorationsProps) => {
  const baseGradient = (
    <div className="absolute inset-0" style={{ background: theme.heroGradient }} />
  )

  return (
    <>
      {baseGradient}
      <BackgroundPattern theme={theme} />
      <FloatingElements theme={theme} />
      <ParticleSystem theme={theme} />
    </>
  )
}

export default {
  BackgroundPattern,
  FloatingElements,
  ParticleSystem,
  HeroDecorations
}
